// src/pages/PlatformIntegrations.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Container,
  Paper,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Language as LanguageIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  NetworkCheck as NetworkCheckIcon,
  Close as CloseIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { getApiUrl } from '../config';
import PageHead from '../components/PageHead';

function PlatformIntegrations() {
  const [wordpress, setWordpress] = useState({
    connected: false,
    url: '',
    isLoading: true,
    error: null
  });
  
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    site_url: '',
    api_key: ''
  });
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Check WordPress connection status on component mount
  useEffect(() => {
    checkWordPressConnection();
  }, []);

  const checkWordPressConnection = async () => {
    try {
      setWordpress(prev => ({ ...prev, isLoading: true, error: null }));
      
      const response = await fetch(`${getApiUrl()}/settings/wordpress-status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch WordPress status');
      }

      const data = await response.json();
      console.log('WordPress status:', data); // Debugging

      setWordpress({
        connected: data.connected,
        url: data.site_url || '',
        isLoading: false,
        error: null
      });
    } catch (err) {
      console.error('Error checking WordPress connection:', err);
      setWordpress(prev => ({
        ...prev,
        connected: false,
        isLoading: false,
        error: err.message
      }));
    }
  };

  const handleConnect = async () => {
    try {
      if (!formData.site_url) {
        setError('Site URL is required');
        return;
      }

      setConnecting(true);
      setError(null);

      // Clean up the URL
      let siteUrl = formData.site_url.trim();
      if (!siteUrl.startsWith('http://') && !siteUrl.startsWith('https://')) {
        siteUrl = 'https://' + siteUrl;
      }
      
      // First, test the connection to get the API key
      const testResponse = await fetch(`${siteUrl}/wp-json/autoblogpilot/v1/test-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!testResponse.ok) {
        throw new Error('Could not connect to WordPress site. Please make sure the AutoBlogPilot plugin is installed and activated.');
      }

      const testData = await testResponse.json();
      
      // Now store the connection with the received API key
      const response = await fetch(`${getApiUrl()}/settings/store-wordpress`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_url: siteUrl,
          api_key: testData.api_key
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to connect WordPress');
      }

      setSuccess('WordPress connected successfully');
      setOpenDialog(false);
      checkWordPressConnection(); // Refresh the connection status
    } catch (err) {
      setError(err.message);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setWordpress(prev => ({ ...prev, isLoading: true }));
      
      const response = await fetch(`${getApiUrl()}/settings/wordpress-disconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect WordPress');
      }

      setSuccess('WordPress disconnected successfully');
      setWordpress({
        connected: false,
        url: '',
        isLoading: false,
        error: null
      });
    } catch (err) {
      setError(err.message);
      setWordpress(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleTestConnection = async () => {
    try {
      setWordpress(prev => ({ ...prev, isLoading: true }));
      await checkWordPressConnection();
      setSuccess('Connection test successful');
    } catch (err) {
      setError('Connection test failed');
    }
  };

  if (wordpress.isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <CircularProgress size={60} thickness={4} />
        </motion.div>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <PageHead 
        title="Platform Integrations" 
        description="Connect and manage your WordPress site with AutoBlogPilot"
      />
      <Box
        sx={{
          background: 'linear-gradient(145deg, #f6f8ff 0%, #f0f4ff 100%)',
          borderRadius: 4,
          p: 4,
          minHeight: '90vh',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box mb={4}>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Platform Integrations
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Connect and manage your WordPress site
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}

          <Paper
            elevation={2}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              background: 'white',
              transition: 'all 0.3s ease',
            }}
          >
            <Box p={3}>
              <Box display="flex" alignItems="center" mb={3}>
                <Box
                  sx={{
                    background: wordpress.connected ? 'rgba(76, 175, 80, 0.1)' : 'rgba(33, 150, 243, 0.1)',
                    borderRadius: '50%',
                    p: 1,
                    mr: 2,
                    color: wordpress.connected ? 'success.main' : 'primary.main',
                  }}
                >
                  <LanguageIcon />
                </Box>
                <Box flex={1}>
                  <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
                    WordPress Integration
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {wordpress.connected ? (
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinkIcon fontSize="small" sx={{ color: 'success.main' }} />
                        Connected to {wordpress.url}
                      </Box>
                    ) : (
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinkOffIcon fontSize="small" color="action" />
                        Not connected
                      </Box>
                    )}
                  </Typography>
                </Box>
                <Box>
                  {wordpress.connected ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Test Connection">
                        <IconButton
                          onClick={handleTestConnection}
                          sx={{ 
                            color: 'primary.main',
                            '&:hover': { background: 'rgba(33, 150, 243, 0.1)' }
                          }}
                        >
                          <NetworkCheckIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Disconnect WordPress">
                        <IconButton
                          onClick={handleDisconnect}
                          sx={{ 
                            color: 'error.main',
                            '&:hover': { background: 'rgba(211, 47, 47, 0.1)' }
                          }}
                        >
                          <LinkOffIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={() => setOpenDialog(true)}
                      startIcon={<LinkIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                        boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                      }}
                    >
                      Connect WordPress
                    </Button>
                  )}
                </Box>
              </Box>

              {wordpress.connected && (
                <Box mt={3}>
                  <Divider sx={{ mb: 3 }} />
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Integration Status
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'rgba(76, 175, 80, 0.05)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                        }}
                      />
                      <Typography>Active and connected</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Your WordPress site is properly connected and ready to receive content.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </motion.div>
      </Box>

      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" component="h2" fontWeight="bold">
              Connect WordPress
            </Typography>
            <IconButton onClick={() => setOpenDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Enter your WordPress site URL to establish the connection. Make sure you have the AutoBlogPilot plugin installed and activated.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="WordPress Site URL"
            placeholder="https://your-site.com"
            value={formData.site_url}
            onChange={(e) => setFormData({ ...formData, site_url: e.target.value })}
            InputProps={{
              startAdornment: (
                <LanguageIcon sx={{ mr: 1, color: 'action.active' }} />
              ),
            }}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            sx={{
              color: 'text.secondary',
              '&:hover': { background: 'rgba(0,0,0,0.05)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConnect}
            variant="contained"
            disabled={connecting}
            startIcon={connecting ? <CircularProgress size={20} /> : <LinkIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
            }}
          >
            {connecting ? 'Connecting...' : 'Connect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default PlatformIntegrations;
